// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import useValidateForm from '@Hooks/useValidateForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { useLocalMessage } from '@Helpers/locale';
import AceEditor from 'react-ace';

interface AnalyzerTemplateDrawerProps {
    data: Record<string, any>;
    title: string;
    mode?: 'edit' | 'add';
    triggerElem: ReactElement;
    onSubmit: (state: any) => Promise<any>;
    className?: string;
}

import 'ace-builds/src-noconflict/mode-handlebars';
import 'ace-builds/src-noconflict/theme-tomorrow';

export const AnalyzerTemplateDrawer = ({
    triggerElem,
    data,
    onSubmit,
    title,
}: AnalyzerTemplateDrawerProps): ReactElement => {
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const i = useLocalMessage();

    const onSubmitHandler = (values: Record<string, any>) => {
        return onSubmit({ ...data, ...values });
    };

    return (
        <THDrawer
            title={title}
            triggerElem={triggerElem}
            className="drawer-form"
            width="55vw"
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={data}
                    onFinish={(values) => {
                        onSubmitHandler(values).then(() => {
                            toggleDrawer();
                            setFieldsTouched(false);
                        });
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    form.resetFields();
                                    toggleDrawer();
                                    setFieldsTouched(false);
                                }}
                                footerText={i('general.confirm')}
                                hidden={!fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item name="content">
                            <AceEditor width="100%" height="40rem" mode={'handlebars'} theme="tomorrow" />
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
